import type { OcrResult } from '../types';
import { displayOcrStatus, type DisplayOcrStatus } from './run-status';

/**
 * Per-PAGE run state for the results panel.
 *
 * A result carries ONE status for every page it covers, but a re-run of
 * selected pages (`rerunning_page_numbers`) and a run that failed on some
 * batches (`failed_page_ranges`) are both page-scoped. Reading the result's
 * status on every page made a two-page re-run look like the whole container
 * was being re-read, and a failed re-run look like every page had failed.
 */
type RunState = Pick<
  OcrResult,
  | 'status'
  | 'rerunning_page_numbers'
  | 'failed_page_ranges'
  | 'progress_percent'
>;

const isInFlightStatus = (status: OcrResult['status']) =>
  status === 'pending' || status === 'processing';

/** Global 1-based pages inside the result's failed ranges, ascending. */
export function failedPageNumbers(result: RunState): number[] {
  const pages = new Set<number>();
  for (const range of result.failed_page_ranges ?? []) {
    for (let p = range.from; p <= range.to; p++) pages.add(p);
  }
  return [...pages].sort((a, b) => a - b);
}

/** Whether THIS page is being extracted right now. */
export function isPageInFlight(result: RunState, pageNumber: number): boolean {
  if (!isInFlightStatus(result.status)) return false;
  const rerunning = result.rerunning_page_numbers ?? [];
  return rerunning.length === 0 || rerunning.includes(pageNumber);
}

/** The status badge one page section shows. */
export function pageDisplayStatus(
  result: RunState,
  pageNumber: number
): DisplayOcrStatus {
  const status = displayOcrStatus(result.status);
  const rerunning = result.rerunning_page_numbers ?? [];

  // A page left out of a re-run is whatever it was before the re-run started.
  if (status === 'processing' && rerunning.length > 0) {
    return rerunning.includes(pageNumber) ? 'processing' : 'completed';
  }

  if (status === 'completed_with_errors') {
    const failed = failedPageNumbers(result);
    if (failed.length > 0) {
      return failed.includes(pageNumber) ? 'failed' : 'completed';
    }
  }

  return status;
}

/**
 * Pages a result WITH fields renders a section for: the pages its fields sit
 * on, plus pages being re-run (their stale fields were stripped) and pages
 * that failed (they produced none) — or either would simply vanish.
 */
export function sectionPageNumbers(
  fieldPages: Iterable<number>,
  result: RunState
): number[] {
  return [
    ...new Set([
      ...fieldPages,
      ...(result.rerunning_page_numbers ?? []),
      ...(result.status === 'completed_with_errors'
        ? failedPageNumbers(result)
        : [])
    ])
  ].sort((a, b) => a - b);
}

/**
 * Pages a result with NO fields fans out into: one section per re-running
 * page, so a new template's sibling re-reading pages 2 and 5 shows loading on
 * both. Empty means "anchor the result once" — a first run in flight.
 */
export function emptyResultSectionPages(result: RunState): number[] {
  if (!isInFlightStatus(result.status)) return [];
  return [...new Set(result.rerunning_page_numbers ?? [])].sort(
    (a, b) => a - b
  );
}

/**
 * One progress number for a multi-template run: siblings that already
 * finished are not part of the work in flight, so they must not pull the bar
 * up — a re-run beside a completed sibling otherwise opened at 50%+. The
 * server scopes each sibling's own `progress_percent` to its re-run.
 */
export function runProgressPercent(results: RunState[]): number | null {
  const inFlight = results.filter((r) => isInFlightStatus(r.status));
  if (inFlight.length === 0) return null;
  const sum = inFlight.reduce((s, r) => s + (r.progress_percent ?? 0), 0);
  return Math.min(100, Math.max(0, Math.round(sum / inFlight.length)));
}

/**
 * The pages a selective re-run is reading right now, across every sibling
 * still in flight. Empty for a full run — every page is in flight there, and
 * the run is described by its templates instead. Naming the templates during
 * a re-run read as "re-reading everything with N templates" while one page
 * was being read.
 */
export function rerunningPagesInFlight(results: RunState[]): number[] {
  const pages = new Set<number>();
  for (const r of results) {
    if (!isInFlightStatus(r.status)) continue;
    for (const p of r.rerunning_page_numbers ?? []) pages.add(p);
  }
  return [...pages].sort((a, b) => a - b);
}

/**
 * A finished result that was dispatched no page at all — an empty
 * `assigned_page_numbers` is an answer (backend rule), e.g. a re-run that
 * moved every page this template held back to another one. It has nothing to
 * render; anchoring it fell back to PG-001 and badged that page as a
 * two-template pool. In flight or failed it still gets its section.
 */
export function coversNoPage(
  result: RunState & Pick<OcrResult, 'assigned_page_numbers'>
): boolean {
  return (
    Array.isArray(result.assigned_page_numbers) &&
    result.assigned_page_numbers.length === 0 &&
    !isInFlightStatus(result.status) &&
    result.status !== 'failed'
  );
}
